import {
  PROMO_REDEMPTION_STATUSES,
  type AdminPromoCampaignView,
  type AdminPromoRedemptionView,
} from '@steado/admin-contract/promotions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArchiveIcon, PencilIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { FieldRow, FieldRows, Panel, StatCards, TabSkeleton } from '@/components/panel/panels';
import { RecordHeader } from '@/components/panel/RecordHeader';
import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { ErrorState } from '@/components/shell/ErrorState';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { BadgeCell, PersonCell, TextCell } from '@/components/table/cells';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime, humanise } from '@/lib/format';

import { PromotionFormDialog } from './PromotionFormDialog';
import { formatMoney, formatRevenue, PLAN_LABELS, SOURCE_LABELS, STATE_TONES } from './promotion-labels';
import {
  archivePromotion,
  promotionQueryOptions,
  PROMOTIONS_KEY,
  REDEMPTIONS_PAGE_SIZE,
  redemptionsQueryOptions,
} from './promotions-query';
import type { PromotionDetailSearch } from './promotions-search';

const ADMIN_ONLY = 'Changing a campaign requires the admin role.';

/** Radix has no empty option, so "every status" needs a value of its own. */
const ALL = '__all__';

const REDEMPTION_COLUMNS: readonly TableColumn[] = [
  { key: 'user', label: 'Person', width: '1.5fr' },
  { key: 'source', label: 'Source', width: '0.9fr' },
  { key: 'status', label: 'Status', width: '0.8fr' },
  { key: 'claimed', label: 'Claimed', width: '0.9fr' },
  { key: 'purchased', label: 'Purchased', width: '1.1fr' },
  { key: 'price', label: 'Paid', width: '1fr', align: 'right' },
  { key: 'premium', label: 'Premium now', width: '0.8fr' },
];

export interface PromotionDetailScreenProps {
  readonly campaignId: string;
  readonly search: PromotionDetailSearch;
  readonly onSearchChange: (next: PromotionDetailSearch) => void;
  readonly onOpenUser: (userId: string) => void;
}

export function PromotionDetailScreen({
  campaignId,
  search,
  onSearchChange,
  onOpenUser,
}: PromotionDetailScreenProps) {
  const { api } = useAuth();
  const query = useQuery(promotionQueryOptions(api, campaignId));

  if (query.isError) {
    return (
      <ErrorState
        title="Could not load the campaign"
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  if (query.isPending) {
    return (
      <div className="flex max-w-[1200px] flex-col gap-3">
        <TabSkeleton rows={2} />
        <TabSkeleton rows={6} />
      </div>
    );
  }

  const campaign = query.data;
  return (
    <div className="flex max-w-[1200px] flex-col gap-3">
      <CampaignHeader campaign={campaign} />
      <StatCards
        stats={[
          { label: 'Claims in app', value: String(campaign.stats.claims) },
          { label: 'Purchases', value: String(campaign.stats.purchases) },
          { label: 'iOS', value: String(campaign.stats.purchasesIos) },
          { label: 'Android', value: String(campaign.stats.purchasesAndroid) },
          {
            label: 'Redeemed in App Store directly',
            value: String(campaign.stats.storeDirect),
            note: 'bought with the code at Apple, never typed in the app',
          },
          { label: 'Still subscribed', value: String(campaign.stats.activeSubscribers) },
        ]}
      />
      <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
        <Panel title="Revenue" description="First purchases, per currency, as the store reported them.">
          <p className="font-mono text-[18px] font-medium text-foreground">
            {formatRevenue(campaign.stats.revenue) ?? '—'}
          </p>
        </Panel>
        <StoreSetupPanel campaign={campaign} />
      </div>
      <RedemptionsTable
        campaignId={campaignId}
        search={search}
        onSearchChange={onSearchChange}
        onOpenUser={onOpenUser}
      />
    </div>
  );
}

function CampaignHeader({ campaign }: { readonly campaign: AdminPromoCampaignView }) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'none' | 'edit' | 'archive'>('none');

  const archive = useMutation({
    mutationFn: () => archivePromotion(api, campaign.id),
    onSuccess: () => {
      setDialog('none');
      void queryClient.invalidateQueries({ queryKey: PROMOTIONS_KEY });
    },
    retry: false,
  });

  const archived = campaign.state === 'archived';

  return (
    <>
      <RecordHeader
        label="Campaign summary"
        title={campaign.name}
        badges={<BadgeCell label={campaign.state} tone={STATE_TONES[campaign.state]} dot />}
        meta={[
          campaign.code,
          campaign.discountLabel,
          PLAN_LABELS[campaign.appliesTo],
          campaign.newSubscribersOnly ? 'New subscribers only' : 'Anyone',
        ]}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={!can.mutate || archived}
              title={can.mutate ? undefined : ADMIN_ONLY}
              onClick={() => {
                setDialog('edit');
              }}
            >
              <PencilIcon aria-hidden="true" className="size-3.5" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!can.mutate || archived || archive.isPending}
              title={can.mutate ? undefined : ADMIN_ONLY}
              onClick={() => {
                setDialog('archive');
              }}
              className="text-err-fg hover:text-err-fg"
            >
              <ArchiveIcon aria-hidden="true" className="size-3.5" />
              Archive
            </Button>
          </>
        }
        stats={[
          {
            label: 'Starts',
            value: campaign.startsAt === null ? 'Right away' : formatDateTime(campaign.startsAt),
          },
          {
            label: 'Ends',
            value: campaign.endsAt === null ? 'No end date' : formatDateTime(campaign.endsAt),
          },
          {
            label: 'Claims',
            value:
              campaign.maxClaims === null
                ? String(campaign.stats.claims)
                : `${String(campaign.stats.claims)}/${String(campaign.maxClaims)}`,
            note: campaign.maxClaims === null ? 'no limit' : 'of the limit',
          },
        ]}
      />

      {dialog === 'edit' ? (
        <PromotionFormDialog
          campaign={campaign}
          onClose={() => {
            setDialog('none');
          }}
        />
      ) : null}

      {dialog === 'archive' ? (
        <ConfirmDialog
          title={`Archive ${campaign.code}?`}
          body={
            <div className="flex flex-col gap-2">
              <p>The app stops accepting the code and the campaign leaves the list. Its numbers are kept.</p>
              <p>
                This does not touch the stores. Deactivate the offer in App Store Connect and Play Console
                too, or the code keeps working there.
              </p>
            </div>
          }
          confirmLabel="Archive"
          destructive
          isPending={archive.isPending}
          {...(archive.isError ? { error: archive.error.message } : {})}
          onConfirm={() => {
            archive.mutate();
          }}
          onCancel={() => {
            setDialog('none');
            archive.reset();
          }}
        />
      ) : null}
    </>
  );
}

function StoreSetupPanel({ campaign }: { readonly campaign: AdminPromoCampaignView }) {
  return (
    <Panel
      title="Store setup"
      description="The offers this campaign points at. Purchases are credited by these ids."
      bleed
    >
      <FieldRows>
        <FieldRow label="Apple offer reference name" value={campaign.appleOfferReference} mono />
        <FieldRow label="Apple custom code" value={campaign.appleCustomCode} mono />
        <FieldRow label="Play base plan id" value={campaign.playBasePlanId} mono />
        <FieldRow label="Play offer id" value={campaign.playOfferId} mono />
      </FieldRows>
    </Panel>
  );
}

interface RedemptionsTableProps {
  readonly campaignId: string;
  readonly search: PromotionDetailSearch;
  readonly onSearchChange: (next: PromotionDetailSearch) => void;
  readonly onOpenUser: (userId: string) => void;
}

function paidOf(row: AdminPromoRedemptionView): string | null {
  if (row.price === null) return null;
  return row.currency === null ? String(row.price) : formatMoney(row.price, row.currency);
}

function RedemptionsTable({ campaignId, search, onSearchChange, onOpenUser }: RedemptionsTableProps) {
  const { api } = useAuth();
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);
  const page = useQuery(redemptionsQueryOptions(api, campaignId, search));

  const onNext = useCallback(() => {
    const next = page.data?.pagination.nextCursor;
    if (next === undefined || next === null) return;
    setCursorStack((stack) => [...stack, search.cursor]);
    onSearchChange({ ...search, cursor: next });
  }, [page.data, search, onSearchChange]);

  const onPrevious = useCallback(() => {
    setCursorStack((stack) => {
      onSearchChange({ ...search, cursor: stack[stack.length - 1] });
      return stack.slice(0, -1);
    });
  }, [search, onSearchChange]);

  const onStatusChange = useCallback(
    (next: string) => {
      setCursorStack([]);
      onSearchChange({
        status: next === ALL ? undefined : (next as PromotionDetailSearch['status']),
        cursor: undefined,
      });
    },
    [onSearchChange],
  );

  const rows: readonly TableRow[] = useMemo(
    () =>
      (page.data?.items ?? []).map((row) => ({
        id: row.id,
        onOpen: () => {
          onOpenUser(row.userId);
        },
        cells: [
          <PersonCell
            key="user"
            name={row.displayName}
            sub={row.phone}
            initials={(row.displayName ?? '??').slice(0, 2).toUpperCase()}
          />,
          <TextCell key="source" value={SOURCE_LABELS[row.source]} />,
          <BadgeCell key="status" label={row.status} tone={row.status === 'purchased' ? 'ok' : 'info'} />,
          <TextCell key="claimed" value={row.claimedAt === null ? null : formatDateTime(row.claimedAt)} />,
          <TextCell
            key="purchased"
            value={row.purchasedAt === null ? null : formatDateTime(row.purchasedAt)}
            sub={[row.platform === null ? null : humanise(row.platform), row.productId]
              .filter((part): part is string => part !== null)
              .join(' · ')}
          />,
          <TextCell key="price" value={paidOf(row)} align="right" mono />,
          <BadgeCell
            key="premium"
            label={row.premiumNow ? 'premium' : 'free'}
            tone={row.premiumNow ? 'purple' : 'mute'}
          />,
        ],
      })),
    [page.data, onOpenUser],
  );

  const isFiltered = search.status !== undefined;
  const pageOffset = cursorStack.length * REDEMPTIONS_PAGE_SIZE;

  if (page.isError) {
    return (
      <ErrorState
        title="Could not load redemptions"
        message={page.error.message}
        onRetry={() => {
          void page.refetch();
        }}
      />
    );
  }

  return (
    <DataTable
      caption="Redemptions"
      title="Redemptions"
      count={
        <Select value={search.status ?? ALL} onValueChange={onStatusChange}>
          <SelectTrigger aria-label="Status" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any status</SelectItem>
            {PROMO_REDEMPTION_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
      columns={REDEMPTION_COLUMNS}
      rows={rows}
      isLoading={page.isPending}
      emptyTitle={isFiltered ? 'No matches' : 'No redemptions yet'}
      emptyBody={
        isFiltered
          ? 'No redemption has this status.'
          : 'Rows appear when someone types the code in the app, or when RevenueCat reports a purchase with this offer.'
      }
      footer={
        <PaginationFooter
          rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
          rangeEnd={pageOffset + rows.length}
          hasPrevious={cursorStack.length > 0}
          hasNext={page.data?.pagination.hasMore ?? false}
          onPrevious={onPrevious}
          onNext={onNext}
          isLoading={page.isPending}
        />
      }
    />
  );
}
